import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  Calculator,
  ArrowRight,
  Paintbrush,
  HardHat,
  LayoutGrid,
  DoorOpen,
  Drill,
  Droplets,
  Sparkles,
  Truck,
  Zap,
  Hammer,
  Trees,
  ChefHat,
  SolarPanel,
  Ruler,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SectionHeading } from "@/components/section-heading";
import { tradePages } from "@/data/trade-pages";
import { jsonLdScript, priceOffersLd, SITE_URL } from "@/lib/json-ld";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLanguage } from "@/lib/i18n";

export const Route = createFileRoute("/preise")({
  staticData: { sitemap: true },
  validateSearch: (
    search: Record<string, unknown>,
  ): { q?: string; service?: string; tab?: string } => ({
    q: typeof search.q === "string" ? search.q : undefined,
    service: typeof search.service === "string" ? search.service : undefined,
    tab: typeof search.tab === "string" ? search.tab : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Baukosten & Richtpreise Schweiz. Begner" },
      {
        name: "description",
        content:
          "Aktuelle Richtpreise für Bau- und Renovationsarbeiten in der Schweiz, von Malerei und Fassade bis Dach, Bodenbelag und Sanitär.",
      },
      { property: "og:title", content: "Baukosten & Richtpreise. Begner" },
      {
        property: "og:description",
        content: "Transparente Richtpreise für Ihr Bauvorhaben in der Schweiz.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    scripts: [
      jsonLdScript({
        "@context": "https://schema.org",
        "@type": "OfferCatalog",
        name: "Richtpreise für Handwerkerarbeiten in der Schweiz",
        url: `${SITE_URL}/preise`,
        itemListElement: tradePages.map((trade) => ({
          "@type": "OfferCatalog",
          name: trade.name,
          url: `${SITE_URL}/gewerke/${trade.slug}`,
          itemListElement: priceOffersLd(trade.prices),
        })),
      }),
    ],
  }),
  component: PreisePage,
});

type PriceRow = {
  service: string;
  unit: string;
  min: number;
  max: number;
  note?: string;
};

const priceTabIcons = {
  malerei: Paintbrush,
  dach: HardHat,
  boden: LayoutGrid,
  fenster: DoorOpen,
  sanitaer: Droplets,
  bau: Drill,
  sanierung: Hammer,
  elektro: Zap,
  reinigung: Sparkles,
  umzug: Truck,
  umgebung: Trees,
  kueche: ChefHat,
  energie: SolarPanel,
  planung: Ruler,
} as const;

const priceCopy = {
  de: {
    locale: "de-CH",
    eyebrow: "Richtpreise",
    title: "Was kostet Ihr Bauvorhaben?",
    intro:
      "Transparente Richtpreise für Bau- und Renovationsarbeiten in der Schweiz. Schätzen Sie die Kosten Ihres Projekts vorab ab und holen Sie anschliessend konkrete Offerten ein.",
    calculatorEyebrow: "Baukostenrechner",
    calculatorTitle: "Schnelle Schätzung in wenigen Sekunden",
    calculatorText:
      "Wählen Sie Leistung und Umfang, wir berechnen einen indikativen Preisrahmen auf Basis aktueller Schweizer Marktdaten.",
    quoteEyebrow: "Genauere Zahlen?",
    quoteTitle: "Holen Sie kostenlos echte Offerten ein",
    quoteText:
      "Beschreiben Sie Ihr Projekt, mehrere geprüfte Auftragnehmer aus Ihrer Region senden Ihnen verbindliche Angebote.",
    quoteButton: "Offerten anfragen",
    tableEyebrow: "Preistabellen",
    tableTitle: "Richtpreise nach Gewerk",
    tableText:
      "Alle Preise sind Bruttowerte in CHF und gelten als Orientierung für die ganze Schweiz.",
    tableHeads: ["Leistung", "Einheit", "Von (CHF)", "Bis (CHF)"],
    calcLabels: {
      service: "Leistung",
      amount: "Menge",
      estimate: "Geschätzter Preisrahmen",
      between: "bis",
      note: "Indikative Schätzung. Echte Offerten können je nach Aufwand, Material und Region abweichen.",
    },
    tabs: [
      {
        id: "malerei",
        label: "Malerei & Trockenbau",
        rows: [
          { service: "Innenwand streichen (Dispersion)", unit: "m²", min: 12, max: 22 },
          { service: "Decke streichen", unit: "m²", min: 13, max: 25 },
          { service: "Tapezieren (Raufaser)", unit: "m²", min: 17, max: 30 },
          { service: "Fassade neu streichen", unit: "m²", min: 34, max: 60, note: "inkl. Gerüst" },
          { service: "Holzfenster lackieren", unit: "Stück", min: 180, max: 340 },
          { service: "Fassadenverkleidung und -dämmung", unit: "m²", min: 95, max: 190 },
          { service: "Fassadensanierung", unit: "m²", min: 120, max: 255 },
          { service: "Trockenbauwand mit Material", unit: "m²", min: 48, max: 150 },
          { service: "Abgehängte Decke mit Material", unit: "m²", min: 50, max: 140 },
          { service: "Trockenbau ohne Material", unit: "m²", min: 22, max: 90 },
          { service: "Aussenputz", unit: "m²", min: 25, max: 40 },
          { service: "Innenputz", unit: "m²", min: 25, max: 40 },
        ],
      },
      {
        id: "dach",
        label: "Dach & Spengler",
        rows: [
          { service: "Ziegeldach neu eindecken", unit: "m²", min: 175, max: 270 },
          { service: "Flachdach abdichten", unit: "m²", min: 110, max: 175 },
          { service: "Dachfenster Velux setzen", unit: "Stück", min: 1200, max: 2300 },
          { service: "Dachrinne / Spenglerarbeit", unit: "m", min: 90, max: 155 },
          { service: "Dachisolation aufbringen", unit: "m²", min: 60, max: 115 },
          { service: "Dachboden ausbauen", unit: "m²", min: 450, max: 645 },
        ],
      },
      {
        id: "sanierung",
        label: "Renovation & Umbau",
        rows: [
          { service: "Wohnung sanieren / renovieren", unit: "m²", min: 740, max: 1400 },
          { service: "Haus komplett renovieren", unit: "m²", min: 1270, max: 2000 },
          {
            service: "Entrümpelung bei Wohnungsrenovierung",
            unit: "m²",
            min: 50,
            max: 65,
          },
        ],
      },
      {
        id: "boden",
        label: "Boden & Platten",
        rows: [
          { service: "Parkett verlegen (geölt)", unit: "m²", min: 85, max: 150 },
          { service: "Vinylboden verlegen", unit: "m²", min: 50, max: 90 },
          { service: "Naturstein / Plattenboden", unit: "m²", min: 125, max: 230 },
          { service: "Teppich verlegen", unit: "m²", min: 40, max: 70 },
          { service: "Estrich aufbringen", unit: "m²", min: 45, max: 80 },
          { service: "Parkett (Material)", unit: "m²", min: 60, max: 100 },
          { service: "Plattenlegerarbeiten", unit: "m²", min: 36, max: 190 },
          { service: "Badezimmerfliesen (Material)", unit: "m²", min: 15, max: 60 },
          { service: "Laminat verlegen", unit: "m²", min: 50, max: 200 },
          { service: "Teppichboden (Material)", unit: "m²", min: 10, max: 60 },
          { service: "Eichenparkett", unit: "m²", min: 100, max: 240 },
          { service: "Parkett schleifen und versiegeln", unit: "m²", min: 30, max: 50 },
        ],
      },
      {
        id: "fenster",
        label: "Fenster & Türen",
        rows: [
          { service: "Holz-Alu-Fenster (Standard)", unit: "Stück", min: 850, max: 1600 },
          { service: "Kunststofffenster", unit: "Stück", min: 550, max: 1100 },
          { service: "Haustür einbauen", unit: "Stück", min: 2800, max: 6500 },
          { service: "Innentür Standard", unit: "Stück", min: 480, max: 1200 },
          { service: "Schiebetür-System", unit: "lfm", min: 950, max: 2200 },
          { service: "Fenster inkl. Montage", unit: "m²", min: 420, max: 680 },
          { service: "Fenster einbauen", unit: "Stück", min: 70, max: 180 },
          { service: "Holzfenster sanieren", unit: "m²", min: 350, max: 430 },
        ],
      },
      {
        id: "sanitaer",
        label: "Sanitär & Heizung",
        rows: [
          { service: "Bad komplett sanieren", unit: "Pauschal", min: 15000, max: 35000 },
          { service: "Wasserleitungen Grob- und Feininstallation", unit: "m²", min: 35, max: 100 },
          { service: "Abwasserleitung inkl. Aussenleitung", unit: "m²", min: 25, max: 70 },
          { service: "WC austauschen", unit: "Stück", min: 480, max: 1100 },
          { service: "Sanitär-Feinmontage", unit: "Stück", min: 60, max: 175 },
          { service: "Wärmepumpe Luft/Wasser", unit: "Anlage", min: 28000, max: 48000 },
          { service: "Heizkörper ersetzen", unit: "Stück", min: 380, max: 950 },
          { service: "Boiler ersetzen", unit: "Stück", min: 1400, max: 3600 },
          { service: "Sanitärarbeiten", unit: "h", min: 80, max: 145 },
          { service: "Sanitärinstallationen", unit: "m", min: 72, max: 180 },
          { service: "Hochtemperatur-Wärmepumpe", unit: "Anlage", min: 15000, max: 41000 },
          { service: "Erd- oder Grundwasserwärmepumpe", unit: "Anlage", min: 21000, max: 73000 },
        ],
      },
      {
        id: "bau",
        label: "Bau & Abbruch",
        rows: [
          { service: "Kernbohrung mit Diamantbohrer", unit: "cm", min: 2.7, max: 13.6 },
          { service: "Betonschneiden", unit: "lfm", min: 50, max: 280 },
          { service: "Kernbohrung für Klimaanlage", unit: "Stück", min: 110, max: 230 },
          { service: "Türöffnung in Beton schneiden", unit: "Pauschal", min: 700, max: 4500 },
          { service: "Aushub / kleinere Abbrucharbeiten", unit: "Pauschal", min: 450, max: 2500 },
          { service: "Einfamilienhaus abbrechen", unit: "m²", min: 250, max: 400 },
          { service: "Schotterweg anlegen", unit: "lfm", min: 150, max: 270 },
          { service: "Keller abdichten und isolieren", unit: "m²", min: 60, max: 75 },
          { service: "Grundplatte wasser- und wärmedämmen", unit: "Stück", min: 2100, max: 4700 },
          { service: "Betonieren", unit: "m³", min: 135, max: 250 },
          { service: "Aushub und Abtransport", unit: "m³", min: 15, max: 43 },
          { service: "Ausgrabung und Abtransport", unit: "m³", min: 52, max: 63 },
          { service: "Mauern mit Ziegeln", unit: "m²", min: 225, max: 320 },
        ],
      },
      {
        id: "elektro",
        label: "Elektrik & Smart Home",
        rows: [
          { service: "Elektroinstallation im Haus", unit: "m²", min: 110, max: 265 },
          { service: "Steckdose / Schalter montieren", unit: "Stück", min: 90, max: 220 },
          { service: "Sicherung / Kleinreparatur", unit: "Stück", min: 130, max: 230 },
          { service: "Sicherheitsnachweis SiNa", unit: "Stück", min: 230, max: 380 },
        ],
      },
      {
        id: "umgebung",
        label: "Garten & Umgebung",
        rows: [
          { service: "Asphaltieren bis 200 m²", unit: "m²", min: 40, max: 120 },
          { service: "Asphaltieren über 200 m²", unit: "m²", min: 35, max: 115 },
          { service: "Privatstrasse bauen", unit: "lfm", min: 670, max: 973 },
          { service: "Rasen mähen", unit: "m²", min: 0.23, max: 0.55 },
          { service: "Einfahrt pflastern", unit: "m²", min: 73, max: 168 },
          {
            service: "Gartengestaltung mit Steinplatten",
            unit: "m²",
            min: 120,
            max: 180,
          },
          { service: "Gartengestaltung", unit: "m²", min: 35, max: 110 },
        ],
      },
      {
        id: "umzug",
        label: "Reinigung & Umzug",
        rows: [
          { service: "Reinigungskraft", unit: "h", min: 30, max: 39.5 },
          { service: "Endreinigung 3-Zimmer-Wohnung", unit: "Pauschal", min: 600, max: 840 },
          { service: "Endreinigung Einfamilienhaus", unit: "Pauschal", min: 1650, max: 2450 },
          { service: "Umzug mit Zügelunternehmen", unit: "Pauschal", min: 1000, max: 2700 },
          { service: "Umzugshelfer", unit: "h", min: 30, max: 70 },
          { service: "Transporter mieten", unit: "Tag", min: 105, max: 260 },
          { service: "Zügelunternehmen", unit: "h", min: 78, max: 200 },
          { service: "Grundreinigung", unit: "m²", min: 5, max: 10 },
          { service: "Teppichreinigung", unit: "m²", min: 15, max: 30 },
          { service: "Wohnungsreinigung", unit: "h", min: 40, max: 95 },
          { service: "Dachreinigung mit Imprägnierung", unit: "m²", min: 6, max: 25 },
          { service: "Fassadenreinigung mit Imprägnierung", unit: "m²", min: 10, max: 60 },
        ],
      },
      {
        id: "kueche",
        label: "Küche & Schreinerei",
        rows: [
          { service: "Einfache Küchenrenovation", unit: "Pauschal", min: 5000, max: 10000 },
          {
            service: "Küchenrenovation inkl. Geräte",
            unit: "Pauschal",
            min: 14000,
            max: 24000,
          },
          { service: "Kompletter Küchenumbau", unit: "Pauschal", min: 24000, max: 45000 },
          { service: "Küchenmontage", unit: "lfm", min: 218, max: 348 },
          { service: "Schreinerarbeiten", unit: "h", min: 85, max: 151 },
          { service: "Einbauschrank nach Mass", unit: "Pauschal", min: 3000, max: 12000 },
        ],
      },
      {
        id: "energie",
        label: "Photovoltaik & Energie",
        rows: [
          {
            service: "Photovoltaikanlage Einfamilienhaus (ca. 8–12 kWp)",
            unit: "Anlage",
            min: 16000,
            max: 28000,
          },
          {
            service: "Batteriespeicher inkl. Installation",
            unit: "kWh",
            min: 600,
            max: 900,
          },
          { service: "Wallbox anschliessen", unit: "Stück", min: 770, max: 1300 },
        ],
      },
      {
        id: "planung",
        label: "Planung & Bauleitung",
        rows: [
          { service: "Architektur & Projektplanung", unit: "h", min: 130, max: 220 },
          { service: "Bauleitung", unit: "h", min: 160, max: 230 },
          { service: "Bauberatung", unit: "h", min: 120, max: 200 },
        ],
      },
    ],
    calcOptions: {
      innenmalerei: { label: "Innenmalerei", unit: "m²", min: 12, max: 22 },
      fassade: { label: "Fassade streichen", unit: "m²", min: 34, max: 60 },
      parkett: { label: "Parkett verlegen", unit: "m²", min: 85, max: 150 },
      dach: { label: "Dach neu eindecken", unit: "m²", min: 175, max: 270 },
      bad: {
        label: "Bad sanieren (pauschal)",
        unit: "Pauschal",
        min: 15000,
        max: 35000,
        fixed: true,
      },
      kernbohrung: { label: "Kernbohrung", unit: "cm", min: 2.7, max: 13.6 },
      betonschneiden: { label: "Betonschneiden", unit: "lfm", min: 50, max: 280 },
      reinigung: { label: "Reinigungskraft", unit: "h", min: 30, max: 39.5 },
      endreinigung: {
        label: "Endreinigung 3-Zimmer-Wohnung",
        unit: "Pauschal",
        min: 600,
        max: 840,
        fixed: true,
      },
      umzug: {
        label: "Umzug mit Zügelunternehmen",
        unit: "Pauschal",
        min: 1000,
        max: 2700,
        fixed: true,
      },
      umzugshelfer: { label: "Umzugshelfer", unit: "h", min: 30, max: 70 },
      sanitaer: { label: "Sanitärinstallation", unit: "m²", min: 35, max: 100 },
      elektriker: { label: "Elektroinstallation", unit: "m²", min: 110, max: 265 },
      abbruch: { label: "Einfamilienhaus abbrechen", unit: "m²", min: 250, max: 400 },
      wohnung_sanieren: { label: "Wohnung sanieren / renovieren", unit: "m²", min: 740, max: 1400 },
      tiefbau: { label: "Schotterweg anlegen", unit: "lfm", min: 150, max: 270 },
      kellerabdichtung: { label: "Keller abdichten und isolieren", unit: "m²", min: 60, max: 75 },
      grundplattendaemmung: {
        label: "Grundplatte wasser- und wärmedämmen",
        unit: "Stück",
        min: 2100,
        max: 4700,
      },
      haus_renovieren: { label: "Haus komplett renovieren", unit: "m²", min: 1270, max: 2000 },
      dachbodenausbau: { label: "Dachboden ausbauen", unit: "m²", min: 450, max: 645 },
      betonieren: { label: "Betonieren", unit: "m³", min: 135, max: 250 },
      aushub: { label: "Aushub und Abtransport", unit: "m³", min: 15, max: 43 },
      erdarbeiten: { label: "Ausgrabung und Abtransport", unit: "m³", min: 52, max: 63 },
      maurer: { label: "Mauern mit Ziegeln", unit: "m²", min: 225, max: 320 },
      entruempelung: {
        label: "Entrümpelung bei Wohnungsrenovierung",
        unit: "m²",
        min: 50,
        max: 65,
      },
      fenster: { label: "Fenster inkl. Montage", unit: "m²", min: 420, max: 680 },
      fenster_einbauen: { label: "Fenster einbauen", unit: "Stück", min: 70, max: 180 },
      fenster_reparatur: { label: "Holzfenster sanieren", unit: "m²", min: 350, max: 430 },
      sanitaer_stunde: { label: "Sanitärarbeiten", unit: "h", min: 80, max: 145 },
      sanitaer_installationen: { label: "Sanitärinstallationen", unit: "m", min: 72, max: 180 },
      waermepumpe_hochtemperatur: {
        label: "Hochtemperatur-Wärmepumpe",
        unit: "Anlage",
        min: 15000,
        max: 41000,
      },
      waermepumpe_erdwaerme: {
        label: "Erd- oder Grundwasserwärmepumpe",
        unit: "Anlage",
        min: 21000,
        max: 73000,
      },
      fassadendaemmung: {
        label: "Fassadenverkleidung und -dämmung",
        unit: "m²",
        min: 95,
        max: 190,
      },
      fassadensanierung: { label: "Fassadensanierung", unit: "m²", min: 120, max: 255 },
      trockenbau: { label: "Trockenbauwand mit Material", unit: "m²", min: 48, max: 150 },
      trockenbaudecke: { label: "Abgehängte Decke mit Material", unit: "m²", min: 50, max: 140 },
      trockenbau_ohne_material: {
        label: "Trockenbau ohne Material",
        unit: "m²",
        min: 22,
        max: 90,
      },
      aussenputz: { label: "Aussenputz", unit: "m²", min: 25, max: 40 },
      innenputz: { label: "Innenputz", unit: "m²", min: 25, max: 40 },
      parkett_material: { label: "Parkett (Material)", unit: "m²", min: 60, max: 100 },
      plattenleger: { label: "Plattenlegerarbeiten", unit: "m²", min: 36, max: 190 },
      fliesen_material: { label: "Badezimmerfliesen (Material)", unit: "m²", min: 15, max: 60 },
      laminat: { label: "Laminat verlegen", unit: "m²", min: 50, max: 200 },
      teppich_material: { label: "Teppichboden (Material)", unit: "m²", min: 10, max: 60 },
      eichenparkett: { label: "Eichenparkett", unit: "m²", min: 100, max: 240 },
      parkett_schleifen: {
        label: "Parkett schleifen und versiegeln",
        unit: "m²",
        min: 30,
        max: 50,
      },
      schreinerei: { label: "Schreinerarbeiten", unit: "h", min: 85, max: 151 },
      kuechenrenovation: {
        label: "Einfache Küchenrenovation",
        unit: "Pauschal",
        min: 5000,
        max: 10000,
        fixed: true,
      },
      photovoltaik: {
        label: "Photovoltaikanlage Einfamilienhaus",
        unit: "Anlage",
        min: 16000,
        max: 28000,
        fixed: true,
      },
      batteriespeicher: { label: "Batteriespeicher", unit: "kWh", min: 600, max: 900 },
      wallbox: { label: "Wallbox anschliessen", unit: "Stück", min: 770, max: 1300 },
      architektur: { label: "Architektur & Projektplanung", unit: "h", min: 130, max: 220 },
      bauleitung: { label: "Bauleitung", unit: "h", min: 160, max: 230 },
      asphaltierung: { label: "Asphaltieren bis 200 m²", unit: "m²", min: 40, max: 120 },
      asphaltierung_gross: { label: "Asphaltieren über 200 m²", unit: "m²", min: 35, max: 115 },
      strassenbau: { label: "Privatstrasse bauen", unit: "lfm", min: 670, max: 973 },
      rasenmaehen: { label: "Rasen mähen", unit: "m²", min: 0.23, max: 0.55 },
      pflasterung: { label: "Einfahrt pflastern", unit: "m²", min: 73, max: 168 },
      gartengestaltung: {
        label: "Gartengestaltung mit Steinplatten",
        unit: "m²",
        min: 120,
        max: 180,
      },
      gartengestaltung_allgemein: { label: "Gartengestaltung", unit: "m²", min: 35, max: 110 },
      fassadenreinigung: {
        label: "Fassadenreinigung mit Imprägnierung",
        unit: "m²",
        min: 10,
        max: 60,
      },
      dachreinigung: {
        label: "Dachreinigung mit Imprägnierung",
        unit: "m²",
        min: 6,
        max: 25,
      },
      umzug_stunde: { label: "Zügelunternehmen", unit: "h", min: 78, max: 200 },
      grundreinigung: { label: "Grundreinigung", unit: "m²", min: 5, max: 10 },
      teppichreinigung: { label: "Teppichreinigung", unit: "m²", min: 15, max: 30 },
      wohnungsreinigung: { label: "Wohnungsreinigung", unit: "h", min: 40, max: 95 },
    },
  },
  fr: {
    locale: "fr-CH",
    eyebrow: "Prix indicatifs",
    title: "Combien coûte votre projet?",
    intro:
      "Des prix indicatifs transparents pour les travaux de construction et de rénovation en Suisse. Estimez les coûts de votre projet, puis demandez des offres concrètes.",
    calculatorEyebrow: "Calculateur de coûts",
    calculatorTitle: "Une estimation rapide en quelques secondes",
    calculatorText:
      "Choisissez la prestation et le volume; nous calculons une fourchette indicative à partir des données actuelles du marché suisse.",
    quoteEyebrow: "Besoin de chiffres précis?",
    quoteTitle: "Demandez gratuitement de vraies offres",
    quoteText:
      "Décrivez votre projet, plusieurs prestataires vérifiés de votre région vous enverront des propositions contraignantes.",
    quoteButton: "Demander des offres",
    tableEyebrow: "Tableaux de prix",
    tableTitle: "Prix indicatifs par corps de métier",
    tableText:
      "Tous les prix sont des montants bruts en CHF et servent d'orientation pour toute la Suisse.",
    tableHeads: ["Prestation", "Unité", "De (CHF)", "À (CHF)"],
    calcLabels: {
      service: "Prestation",
      amount: "Quantité",
      estimate: "Fourchette estimée",
      between: "à",
      note: "Estimation indicative. Les offres réelles peuvent varier selon le travail, les matériaux et la région.",
    },
    tabs: [
      {
        id: "malerei",
        label: "Peinture & plâtrerie",
        rows: [
          { service: "Peindre un mur intérieur (dispersion)", unit: "m²", min: 12, max: 22 },
          { service: "Peindre un plafond", unit: "m²", min: 13, max: 25 },
          { service: "Poser du papier peint", unit: "m²", min: 17, max: 30 },
          {
            service: "Repeindre une façade",
            unit: "m²",
            min: 34,
            max: 60,
            note: "échafaudage inclus",
          },
          { service: "Vernir une fenêtre en bois", unit: "pièce", min: 180, max: 340 },
          { service: "Bardage et isolation de façade", unit: "m²", min: 95, max: 190 },
          { service: "Rénovation de façade", unit: "m²", min: 120, max: 255 },
          { service: "Cloison sèche avec matériaux", unit: "m²", min: 48, max: 150 },
          { service: "Faux plafond avec matériaux", unit: "m²", min: 50, max: 140 },
          { service: "Cloison sèche sans matériaux", unit: "m²", min: 22, max: 90 },
          { service: "Crépi extérieur", unit: "m²", min: 25, max: 40 },
          { service: "Crépi intérieur", unit: "m²", min: 25, max: 40 },
        ],
      },
      {
        id: "dach",
        label: "Toiture & ferblanterie",
        rows: [
          { service: "Refaire une toiture en tuiles", unit: "m²", min: 175, max: 270 },
          { service: "Étanchéifier un toit plat", unit: "m²", min: 110, max: 175 },
          { service: "Installer une fenêtre de toit Velux", unit: "pièce", min: 1200, max: 2300 },
          { service: "Gouttière / ferblanterie", unit: "m", min: 90, max: 155 },
          { service: "Poser une isolation de toiture", unit: "m²", min: 60, max: 115 },
          { service: "Aménager les combles", unit: "m²", min: 450, max: 645 },
        ],
      },
      {
        id: "sanierung",
        label: "Rénovation & transformation",
        rows: [
          { service: "Rénover un appartement", unit: "m²", min: 740, max: 1400 },
          { service: "Rénover entièrement une maison", unit: "m²", min: 1270, max: 2000 },
          {
            service: "Débarras lors d'une rénovation",
            unit: "m²",
            min: 50,
            max: 65,
          },
        ],
      },
      {
        id: "boden",
        label: "Sols & carrelage",
        rows: [
          { service: "Poser du parquet huilé", unit: "m²", min: 85, max: 150 },
          { service: "Poser un sol vinyle", unit: "m²", min: 50, max: 90 },
          { service: "Pierre naturelle / carrelage", unit: "m²", min: 125, max: 230 },
          { service: "Poser de la moquette", unit: "m²", min: 40, max: 70 },
          { service: "Réaliser une chape", unit: "m²", min: 45, max: 80 },
          { service: "Parquet (matériau)", unit: "m²", min: 60, max: 100 },
          { service: "Travaux de carreleur", unit: "m²", min: 36, max: 190 },
          { service: "Carrelage de salle de bain (matériau)", unit: "m²", min: 15, max: 60 },
          { service: "Poser du stratifié", unit: "m²", min: 50, max: 200 },
          { service: "Moquette (matériau)", unit: "m²", min: 10, max: 60 },
          { service: "Parquet en chêne", unit: "m²", min: 100, max: 240 },
          { service: "Poncer et vitrifier le parquet", unit: "m²", min: 30, max: 50 },
        ],
      },
      {
        id: "fenster",
        label: "Fenêtres & portes",
        rows: [
          { service: "Fenêtre bois-aluminium standard", unit: "pièce", min: 850, max: 1600 },
          { service: "Fenêtre PVC", unit: "pièce", min: 550, max: 1100 },
          { service: "Installer une porte d'entrée", unit: "pièce", min: 2800, max: 6500 },
          { service: "Porte intérieure standard", unit: "pièce", min: 480, max: 1200 },
          { service: "Système de porte coulissante", unit: "ml", min: 950, max: 2200 },
          { service: "Fenêtre avec pose", unit: "m²", min: 420, max: 680 },
          { service: "Poser une fenêtre", unit: "pièce", min: 70, max: 180 },
          { service: "Rénover une fenêtre en bois", unit: "m²", min: 350, max: 430 },
        ],
      },
      {
        id: "sanitaer",
        label: "Sanitaire & chauffage",
        rows: [
          {
            service: "Rénover entièrement une salle de bain",
            unit: "forfait",
            min: 15000,
            max: 35000,
          },
          {
            service: "Installation sanitaire eau froide/chaude",
            unit: "m²",
            min: 35,
            max: 100,
          },
          { service: "Conduites d'évacuation", unit: "m²", min: 25, max: 70 },
          { service: "Remplacer un WC", unit: "pièce", min: 480, max: 1100 },
          { service: "Montage final sanitaire", unit: "pièce", min: 60, max: 175 },
          { service: "Pompe à chaleur air/eau", unit: "installation", min: 28000, max: 48000 },
          { service: "Remplacer un radiateur", unit: "pièce", min: 380, max: 950 },
          { service: "Remplacer un boiler", unit: "pièce", min: 1400, max: 3600 },
          { service: "Travaux sanitaires", unit: "h", min: 80, max: 145 },
          { service: "Installations sanitaires", unit: "m", min: 72, max: 180 },
          {
            service: "Pompe à chaleur haute température",
            unit: "installation",
            min: 15000,
            max: 41000,
          },
          { service: "Pompe à chaleur sol/eau", unit: "installation", min: 21000, max: 73000 },
        ],
      },
      {
        id: "bau",
        label: "Construction & démolition",
        rows: [
          { service: "Carottage diamant", unit: "cm", min: 2.7, max: 13.6 },
          { service: "Sciage du béton", unit: "ml", min: 50, max: 280 },
          { service: "Carottage pour climatisation", unit: "pièce", min: 110, max: 230 },
          { service: "Ouverture de porte dans le béton", unit: "forfait", min: 700, max: 4500 },
          { service: "Petite démolition / excavation", unit: "forfait", min: 450, max: 2500 },
          { service: "Démolir une maison individuelle", unit: "m²", min: 250, max: 400 },
          { service: "Créer un chemin en gravier", unit: "ml", min: 150, max: 270 },
          { service: "Étanchéifier et isoler une cave", unit: "m²", min: 60, max: 75 },
          { service: "Isoler une dalle de fondation", unit: "pièce", min: 2100, max: 4700 },
          { service: "Bétonnage", unit: "m³", min: 135, max: 250 },
          { service: "Excavation et évacuation", unit: "m³", min: 15, max: 43 },
          { service: "Terrassement et évacuation", unit: "m³", min: 52, max: 63 },
          { service: "Maçonnerie en briques", unit: "m²", min: 225, max: 320 },
        ],
      },
      {
        id: "elektro",
        label: "Électricité & domotique",
        rows: [
          { service: "Installation électrique maison", unit: "m²", min: 110, max: 265 },
          { service: "Installer prise / interrupteur", unit: "pièce", min: 90, max: 220 },
          { service: "Fusible / petite réparation", unit: "pièce", min: 130, max: 230 },
          { service: "Attestation de sécurité SiNa", unit: "pièce", min: 230, max: 380 },
        ],
      },
      {
        id: "umgebung",
        label: "Jardin & aménagements extérieurs",
        rows: [
          { service: "Asphalter jusqu'à 200 m²", unit: "m²", min: 40, max: 120 },
          { service: "Asphalter plus de 200 m²", unit: "m²", min: 35, max: 115 },
          { service: "Construire une route privée", unit: "ml", min: 670, max: 973 },
          { service: "Tondre la pelouse", unit: "m²", min: 0.23, max: 0.55 },
          { service: "Paver une entrée", unit: "m²", min: 73, max: 168 },
          {
            service: "Aménagement de jardin avec dalles",
            unit: "m²",
            min: 120,
            max: 180,
          },
          { service: "Aménagement de jardin", unit: "m²", min: 35, max: 110 },
        ],
      },
      {
        id: "umzug",
        label: "Nettoyage & déménagement",
        rows: [
          { service: "Agent de nettoyage", unit: "h", min: 30, max: 39.5 },
          { service: "Nettoyage final appartement 3 pièces", unit: "forfait", min: 600, max: 840 },
          { service: "Nettoyage final maison individuelle", unit: "forfait", min: 1650, max: 2450 },
          { service: "Déménagement avec entreprise", unit: "forfait", min: 1000, max: 2700 },
          { service: "Aide au déménagement", unit: "h", min: 30, max: 70 },
          { service: "Location de camionnette", unit: "jour", min: 105, max: 260 },
          { service: "Entreprise de déménagement", unit: "h", min: 78, max: 200 },
          { service: "Nettoyage en profondeur", unit: "m²", min: 5, max: 10 },
          { service: "Nettoyage de moquette", unit: "m²", min: 15, max: 30 },
          { service: "Nettoyage d'appartement", unit: "h", min: 40, max: 95 },
          { service: "Nettoyage de toiture avec imprégnation", unit: "m²", min: 6, max: 25 },
          { service: "Nettoyage de façade avec imprégnation", unit: "m²", min: 10, max: 60 },
        ],
      },
      {
        id: "kueche",
        label: "Cuisine & menuiserie",
        rows: [
          { service: "Rénovation simple de cuisine", unit: "forfait", min: 5000, max: 10000 },
          {
            service: "Rénovation de cuisine avec appareils",
            unit: "forfait",
            min: 14000,
            max: 24000,
          },
          {
            service: "Transformation complète de cuisine",
            unit: "forfait",
            min: 24000,
            max: 45000,
          },
          { service: "Montage de cuisine", unit: "ml", min: 218, max: 348 },
          { service: "Travaux de menuiserie", unit: "h", min: 85, max: 151 },
          { service: "Armoire encastrée sur mesure", unit: "forfait", min: 3000, max: 12000 },
        ],
      },
      {
        id: "energie",
        label: "Photovoltaïque & énergie",
        rows: [
          {
            service: "Installation photovoltaïque pour maison (env. 8–12 kWc)",
            unit: "installation",
            min: 16000,
            max: 28000,
          },
          {
            service: "Batterie avec installation",
            unit: "kWh",
            min: 600,
            max: 900,
          },
          { service: "Raccorder une borne de recharge", unit: "pièce", min: 770, max: 1300 },
        ],
      },
      {
        id: "planung",
        label: "Planification & direction des travaux",
        rows: [
          { service: "Architecture & planification de projet", unit: "h", min: 130, max: 220 },
          { service: "Direction des travaux", unit: "h", min: 160, max: 230 },
          { service: "Conseil en construction", unit: "h", min: 120, max: 200 },
        ],
      },
    ],
    calcOptions: {
      innenmalerei: { label: "Peinture intérieure", unit: "m²", min: 12, max: 22 },
      fassade: { label: "Peindre une façade", unit: "m²", min: 34, max: 60 },
      parkett: { label: "Poser du parquet", unit: "m²", min: 85, max: 150 },
      dach: { label: "Refaire une toiture", unit: "m²", min: 175, max: 270 },
      bad: {
        label: "Rénover une salle de bain",
        unit: "forfait",
        min: 15000,
        max: 35000,
        fixed: true,
      },
      kernbohrung: { label: "Carottage", unit: "cm", min: 2.7, max: 13.6 },
      betonschneiden: { label: "Sciage du béton", unit: "ml", min: 50, max: 280 },
      reinigung: { label: "Agent de nettoyage", unit: "h", min: 30, max: 39.5 },
      endreinigung: {
        label: "Nettoyage final 3 pièces",
        unit: "forfait",
        min: 600,
        max: 840,
        fixed: true,
      },
      umzug: {
        label: "Déménagement avec entreprise",
        unit: "forfait",
        min: 1000,
        max: 2700,
        fixed: true,
      },
      umzugshelfer: { label: "Aide au déménagement", unit: "h", min: 30, max: 70 },
      sanitaer: { label: "Installation sanitaire", unit: "m²", min: 35, max: 100 },
      elektriker: { label: "Installation électrique", unit: "m²", min: 110, max: 265 },
      abbruch: { label: "Démolir une maison individuelle", unit: "m²", min: 250, max: 400 },
      wohnung_sanieren: { label: "Rénover un appartement", unit: "m²", min: 740, max: 1400 },
      tiefbau: { label: "Créer un chemin en gravier", unit: "ml", min: 150, max: 270 },
      kellerabdichtung: { label: "Étanchéifier et isoler une cave", unit: "m²", min: 60, max: 75 },
      grundplattendaemmung: {
        label: "Isoler une dalle de fondation",
        unit: "pièce",
        min: 2100,
        max: 4700,
      },
      haus_renovieren: {
        label: "Rénover entièrement une maison",
        unit: "m²",
        min: 1270,
        max: 2000,
      },
      dachbodenausbau: { label: "Aménager les combles", unit: "m²", min: 450, max: 645 },
      betonieren: { label: "Bétonnage", unit: "m³", min: 135, max: 250 },
      aushub: { label: "Excavation et évacuation", unit: "m³", min: 15, max: 43 },
      erdarbeiten: { label: "Terrassement et évacuation", unit: "m³", min: 52, max: 63 },
      maurer: { label: "Maçonnerie en briques", unit: "m²", min: 225, max: 320 },
      entruempelung: {
        label: "Débarras lors d'une rénovation",
        unit: "m²",
        min: 50,
        max: 65,
      },
      fenster: { label: "Fenêtre avec pose", unit: "m²", min: 420, max: 680 },
      fenster_einbauen: { label: "Poser une fenêtre", unit: "pièce", min: 70, max: 180 },
      fenster_reparatur: {
        label: "Rénover une fenêtre en bois",
        unit: "m²",
        min: 350,
        max: 430,
      },
      sanitaer_stunde: { label: "Travaux sanitaires", unit: "h", min: 80, max: 145 },
      sanitaer_installationen: { label: "Installations sanitaires", unit: "m", min: 72, max: 180 },
      waermepumpe_hochtemperatur: {
        label: "Pompe à chaleur haute température",
        unit: "installation",
        min: 15000,
        max: 41000,
      },
      waermepumpe_erdwaerme: {
        label: "Pompe à chaleur sol/eau",
        unit: "installation",
        min: 21000,
        max: 73000,
      },
      fassadendaemmung: { label: "Bardage et isolation de façade", unit: "m²", min: 95, max: 190 },
      fassadensanierung: { label: "Rénovation de façade", unit: "m²", min: 120, max: 255 },
      trockenbau: { label: "Cloison sèche avec matériaux", unit: "m²", min: 48, max: 150 },
      trockenbaudecke: { label: "Faux plafond avec matériaux", unit: "m²", min: 50, max: 140 },
      trockenbau_ohne_material: {
        label: "Cloison sèche sans matériaux",
        unit: "m²",
        min: 22,
        max: 90,
      },
      aussenputz: { label: "Crépi extérieur", unit: "m²", min: 25, max: 40 },
      innenputz: { label: "Crépi intérieur", unit: "m²", min: 25, max: 40 },
      parkett_material: { label: "Parquet (matériau)", unit: "m²", min: 60, max: 100 },
      plattenleger: { label: "Travaux de carreleur", unit: "m²", min: 36, max: 190 },
      fliesen_material: {
        label: "Carrelage de salle de bain (matériau)",
        unit: "m²",
        min: 15,
        max: 60,
      },
      laminat: { label: "Poser du stratifié", unit: "m²", min: 50, max: 200 },
      teppich_material: { label: "Moquette (matériau)", unit: "m²", min: 10, max: 60 },
      eichenparkett: { label: "Parquet en chêne", unit: "m²", min: 100, max: 240 },
      parkett_schleifen: {
        label: "Poncer et vitrifier le parquet",
        unit: "m²",
        min: 30,
        max: 50,
      },
      schreinerei: { label: "Travaux de menuiserie", unit: "h", min: 85, max: 151 },
      kuechenrenovation: {
        label: "Rénovation simple de cuisine",
        unit: "forfait",
        min: 5000,
        max: 10000,
        fixed: true,
      },
      photovoltaik: {
        label: "Installation photovoltaïque pour maison",
        unit: "installation",
        min: 16000,
        max: 28000,
        fixed: true,
      },
      batteriespeicher: { label: "Batterie avec installation", unit: "kWh", min: 600, max: 900 },
      wallbox: { label: "Raccorder une borne de recharge", unit: "pièce", min: 770, max: 1300 },
      architektur: {
        label: "Architecture & planification de projet",
        unit: "h",
        min: 130,
        max: 220,
      },
      bauleitung: { label: "Direction des travaux", unit: "h", min: 160, max: 230 },
      asphaltierung: { label: "Asphalter jusqu'à 200 m²", unit: "m²", min: 40, max: 120 },
      asphaltierung_gross: { label: "Asphalter plus de 200 m²", unit: "m²", min: 35, max: 115 },
      strassenbau: { label: "Construire une route privée", unit: "ml", min: 670, max: 973 },
      rasenmaehen: { label: "Tondre la pelouse", unit: "m²", min: 0.23, max: 0.55 },
      pflasterung: { label: "Paver une entrée", unit: "m²", min: 73, max: 168 },
      gartengestaltung: {
        label: "Aménagement de jardin avec dalles",
        unit: "m²",
        min: 120,
        max: 180,
      },
      gartengestaltung_allgemein: { label: "Aménagement de jardin", unit: "m²", min: 35, max: 110 },
      fassadenreinigung: {
        label: "Nettoyage de façade avec imprégnation",
        unit: "m²",
        min: 10,
        max: 60,
      },
      dachreinigung: {
        label: "Nettoyage de toiture avec imprégnation",
        unit: "m²",
        min: 6,
        max: 25,
      },
      umzug_stunde: { label: "Entreprise de déménagement", unit: "h", min: 78, max: 200 },
      grundreinigung: { label: "Nettoyage en profondeur", unit: "m²", min: 5, max: 10 },
      teppichreinigung: { label: "Nettoyage de moquette", unit: "m²", min: 15, max: 30 },
      wohnungsreinigung: { label: "Nettoyage d'appartement", unit: "h", min: 40, max: 95 },
    },
  },
  it: {
    locale: "it-CH",
    eyebrow: "Prezzi indicativi",
    title: "Quanto costa il suo progetto?",
    intro:
      "Prezzi indicativi trasparenti per lavori di costruzione e ristrutturazione in Svizzera. Stimi i costi del progetto e richieda poi offerte concrete.",
    calculatorEyebrow: "Calcolatore costi",
    calculatorTitle: "Stima rapida in pochi secondi",
    calculatorText:
      "Scelga prestazione e quantità; calcoliamo una fascia indicativa sulla base dei dati attuali del mercato svizzero.",
    quoteEyebrow: "Servono cifre più precise?",
    quoteTitle: "Richieda gratuitamente offerte reali",
    quoteText:
      "Descriva il progetto; più imprese verificate della sua regione le invieranno offerte vincolanti.",
    quoteButton: "Richiedi offerte",
    tableEyebrow: "Tabelle prezzi",
    tableTitle: "Prezzi indicativi per categoria",
    tableText:
      "Tutti i prezzi sono valori lordi in CHF e valgono come orientamento per tutta la Svizzera.",
    tableHeads: ["Prestazione", "Unità", "Da (CHF)", "A (CHF)"],
    calcLabels: {
      service: "Prestazione",
      amount: "Quantità",
      estimate: "Fascia di prezzo stimata",
      between: "a",
      note: "Stima indicativa. Le offerte reali possono variare in base a lavoro, materiale e regione.",
    },
    tabs: [
      {
        id: "malerei",
        label: "Pittura & cartongesso",
        rows: [
          { service: "Tinteggiare parete interna (dispersione)", unit: "m²", min: 12, max: 22 },
          { service: "Tinteggiare soffitto", unit: "m²", min: 13, max: 25 },
          { service: "Tappezzare", unit: "m²", min: 17, max: 30 },
          {
            service: "Ridipingere facciata",
            unit: "m²",
            min: 34,
            max: 60,
            note: "ponteggio incluso",
          },
          { service: "Laccare finestra in legno", unit: "pezzo", min: 180, max: 340 },
          { service: "Rivestimento e isolamento facciata", unit: "m²", min: 95, max: 190 },
          { service: "Risanamento della facciata", unit: "m²", min: 120, max: 255 },
          { service: "Parete in cartongesso con materiale", unit: "m²", min: 48, max: 150 },
          { service: "Controsoffitto con materiale", unit: "m²", min: 50, max: 140 },
          { service: "Cartongesso senza materiale", unit: "m²", min: 22, max: 90 },
          { service: "Intonaco esterno", unit: "m²", min: 25, max: 40 },
          { service: "Intonaco interno", unit: "m²", min: 25, max: 40 },
        ],
      },
      {
        id: "dach",
        label: "Tetti & lattoneria",
        rows: [
          { service: "Rifare copertura in tegole", unit: "m²", min: 175, max: 270 },
          { service: "Impermeabilizzare tetto piano", unit: "m²", min: 110, max: 175 },
          { service: "Installare finestra da tetto Velux", unit: "pezzo", min: 1200, max: 2300 },
          { service: "Grondaia / lattoneria", unit: "m", min: 90, max: 155 },
          { service: "Applicare isolamento tetto", unit: "m²", min: 60, max: 115 },
          { service: "Ristrutturare la mansarda", unit: "m²", min: 450, max: 645 },
        ],
      },
      {
        id: "sanierung",
        label: "Ristrutturazione & trasformazione",
        rows: [
          { service: "Ristrutturare un appartamento", unit: "m²", min: 740, max: 1400 },
          { service: "Ristrutturare completamente una casa", unit: "m²", min: 1270, max: 2000 },
          {
            service: "Sgombero durante la ristrutturazione",
            unit: "m²",
            min: 50,
            max: 65,
          },
        ],
      },
      {
        id: "boden",
        label: "Pavimenti & piastrelle",
        rows: [
          { service: "Posare parquet oliato", unit: "m²", min: 85, max: 150 },
          { service: "Posare pavimento vinilico", unit: "m²", min: 50, max: 90 },
          { service: "Pietra naturale / piastrelle", unit: "m²", min: 125, max: 230 },
          { service: "Posare moquette", unit: "m²", min: 40, max: 70 },
          { service: "Realizzare massetto", unit: "m²", min: 45, max: 80 },
          { service: "Parquet (materiale)", unit: "m²", min: 60, max: 100 },
          { service: "Lavori di piastrellista", unit: "m²", min: 36, max: 190 },
          { service: "Piastrelle bagno (materiale)", unit: "m²", min: 15, max: 60 },
          { service: "Posare laminato", unit: "m²", min: 50, max: 200 },
          { service: "Moquette (materiale)", unit: "m²", min: 10, max: 60 },
          { service: "Parquet in rovere", unit: "m²", min: 100, max: 240 },
          { service: "Levigare e sigillare il parquet", unit: "m²", min: 30, max: 50 },
        ],
      },
      {
        id: "fenster",
        label: "Finestre & porte",
        rows: [
          { service: "Finestra legno-alluminio standard", unit: "pezzo", min: 850, max: 1600 },
          { service: "Finestra in PVC", unit: "pezzo", min: 550, max: 1100 },
          { service: "Installare porta d'ingresso", unit: "pezzo", min: 2800, max: 6500 },
          { service: "Porta interna standard", unit: "pezzo", min: 480, max: 1200 },
          { service: "Sistema porta scorrevole", unit: "ml", min: 950, max: 2200 },
          { service: "Finestra con montaggio", unit: "m²", min: 420, max: 680 },
          { service: "Montare una finestra", unit: "pezzo", min: 70, max: 180 },
          { service: "Ristrutturare finestra in legno", unit: "m²", min: 350, max: 430 },
        ],
      },
      {
        id: "sanitaer",
        label: "Sanitari & riscaldamento",
        rows: [
          { service: "Ristrutturare bagno completo", unit: "forfait", min: 15000, max: 35000 },
          { service: "Impianto sanitario acqua calda/fredda", unit: "m²", min: 35, max: 100 },
          { service: "Condotte di scarico", unit: "m²", min: 25, max: 70 },
          { service: "Sostituire WC", unit: "pezzo", min: 480, max: 1100 },
          { service: "Montaggio finale sanitari", unit: "pezzo", min: 60, max: 175 },
          { service: "Pompa di calore aria/acqua", unit: "impianto", min: 28000, max: 48000 },
          { service: "Sostituire radiatore", unit: "pezzo", min: 380, max: 950 },
          { service: "Sostituire boiler", unit: "pezzo", min: 1400, max: 3600 },
          { service: "Lavori sanitari", unit: "h", min: 80, max: 145 },
          { service: "Installazioni sanitarie", unit: "m", min: 72, max: 180 },
          {
            service: "Pompa di calore ad alta temperatura",
            unit: "impianto",
            min: 15000,
            max: 41000,
          },
          { service: "Pompa di calore geotermica", unit: "impianto", min: 21000, max: 73000 },
        ],
      },
      {
        id: "bau",
        label: "Costruzione & demolizione",
        rows: [
          { service: "Carotaggio diamantato", unit: "cm", min: 2.7, max: 13.6 },
          { service: "Taglio del calcestruzzo", unit: "ml", min: 50, max: 280 },
          { service: "Carotaggio per climatizzatore", unit: "pezzo", min: 110, max: 230 },
          { service: "Apertura porta nel calcestruzzo", unit: "forfait", min: 700, max: 4500 },
          { service: "Piccola demolizione / scavo", unit: "forfait", min: 450, max: 2500 },
          { service: "Demolire una casa unifamiliare", unit: "m²", min: 250, max: 400 },
          { service: "Realizzare un sentiero in ghiaia", unit: "ml", min: 150, max: 270 },
          { service: "Impermeabilizzare e isolare la cantina", unit: "m²", min: 60, max: 75 },
          { service: "Isolare la platea di fondazione", unit: "pezzo", min: 2100, max: 4700 },
          { service: "Getto di calcestruzzo", unit: "m³", min: 135, max: 250 },
          { service: "Scavo e smaltimento", unit: "m³", min: 15, max: 43 },
          { service: "Movimento terra e smaltimento", unit: "m³", min: 52, max: 63 },
          { service: "Muratura in mattoni", unit: "m²", min: 225, max: 320 },
        ],
      },
      {
        id: "elektro",
        label: "Impianti elettrici & domotica",
        rows: [
          { service: "Impianto elettrico casa", unit: "m²", min: 110, max: 265 },
          { service: "Installare presa / interruttore", unit: "pezzo", min: 90, max: 220 },
          { service: "Fusibile / piccola riparazione", unit: "pezzo", min: 130, max: 230 },
          { service: "Certificato di sicurezza SiNa", unit: "pezzo", min: 230, max: 380 },
        ],
      },
      {
        id: "umgebung",
        label: "Giardino & sistemazioni esterne",
        rows: [
          { service: "Asfaltare fino a 200 m²", unit: "m²", min: 40, max: 120 },
          { service: "Asfaltare oltre 200 m²", unit: "m²", min: 35, max: 115 },
          { service: "Costruire una strada privata", unit: "ml", min: 670, max: 973 },
          { service: "Tagliare il prato", unit: "m²", min: 0.23, max: 0.55 },
          { service: "Pavimentare un vialetto", unit: "m²", min: 73, max: 168 },
          {
            service: "Progettazione giardino con lastre",
            unit: "m²",
            min: 120,
            max: 180,
          },
          { service: "Progettazione del giardino", unit: "m²", min: 35, max: 110 },
        ],
      },
      {
        id: "umzug",
        label: "Pulizia & trasloco",
        rows: [
          { service: "Addetto alle pulizie", unit: "h", min: 30, max: 39.5 },
          { service: "Pulizia finale appartamento 3 locali", unit: "forfait", min: 600, max: 840 },
          { service: "Pulizia finale casa unifamiliare", unit: "forfait", min: 1650, max: 2450 },
          { service: "Trasloco con impresa", unit: "forfait", min: 1000, max: 2700 },
          { service: "Aiuto trasloco", unit: "h", min: 30, max: 70 },
          { service: "Noleggio furgone", unit: "giorno", min: 105, max: 260 },
          { service: "Impresa di traslochi", unit: "h", min: 78, max: 200 },
          { service: "Pulizia approfondita", unit: "m²", min: 5, max: 10 },
          { service: "Pulizia della moquette", unit: "m²", min: 15, max: 30 },
          { service: "Pulizia dell'appartamento", unit: "h", min: 40, max: 95 },
          { service: "Pulizia tetto con impregnazione", unit: "m²", min: 6, max: 25 },
          { service: "Pulizia facciata con impregnazione", unit: "m²", min: 10, max: 60 },
        ],
      },
      {
        id: "kueche",
        label: "Cucine & falegnameria",
        rows: [
          { service: "Ristrutturazione semplice cucina", unit: "forfait", min: 5000, max: 10000 },
          {
            service: "Ristrutturazione cucina con elettrodomestici",
            unit: "forfait",
            min: 14000,
            max: 24000,
          },
          { service: "Ristrutturazione completa cucina", unit: "forfait", min: 24000, max: 45000 },
          { service: "Montaggio cucina", unit: "ml", min: 218, max: 348 },
          { service: "Lavori di falegnameria", unit: "h", min: 85, max: 151 },
          { service: "Armadio a muro su misura", unit: "forfait", min: 3000, max: 12000 },
        ],
      },
      {
        id: "energie",
        label: "Fotovoltaico & energia",
        rows: [
          {
            service: "Impianto fotovoltaico per casa (ca. 8–12 kWp)",
            unit: "impianto",
            min: 16000,
            max: 28000,
          },
          {
            service: "Batteria con installazione",
            unit: "kWh",
            min: 600,
            max: 900,
          },
          { service: "Collegare wallbox", unit: "pezzo", min: 770, max: 1300 },
        ],
      },
      {
        id: "planung",
        label: "Progettazione & direzione lavori",
        rows: [
          { service: "Architettura & progettazione", unit: "h", min: 130, max: 220 },
          { service: "Direzione lavori", unit: "h", min: 160, max: 230 },
          { service: "Consulenza edilizia", unit: "h", min: 120, max: 200 },
        ],
      },
    ],
    calcOptions: {
      innenmalerei: { label: "Pittura interna", unit: "m²", min: 12, max: 22 },
      fassade: { label: "Tinteggiare facciata", unit: "m²", min: 34, max: 60 },
      parkett: { label: "Posare parquet", unit: "m²", min: 85, max: 150 },
      dach: { label: "Rifare tetto", unit: "m²", min: 175, max: 270 },
      bad: { label: "Ristrutturare bagno", unit: "forfait", min: 15000, max: 35000, fixed: true },
      kernbohrung: { label: "Carotaggio", unit: "cm", min: 2.7, max: 13.6 },
      betonschneiden: { label: "Taglio calcestruzzo", unit: "ml", min: 50, max: 280 },
      reinigung: { label: "Addetto alle pulizie", unit: "h", min: 30, max: 39.5 },
      endreinigung: {
        label: "Pulizia finale 3 locali",
        unit: "forfait",
        min: 600,
        max: 840,
        fixed: true,
      },
      umzug: { label: "Trasloco con impresa", unit: "forfait", min: 1000, max: 2700, fixed: true },
      umzugshelfer: { label: "Aiuto trasloco", unit: "h", min: 30, max: 70 },
      sanitaer: { label: "Impianto sanitario", unit: "m²", min: 35, max: 100 },
      elektriker: { label: "Impianto elettrico", unit: "m²", min: 110, max: 265 },
      abbruch: { label: "Demolire una casa unifamiliare", unit: "m²", min: 250, max: 400 },
      wohnung_sanieren: { label: "Ristrutturare un appartamento", unit: "m²", min: 740, max: 1400 },
      tiefbau: { label: "Realizzare un sentiero in ghiaia", unit: "ml", min: 150, max: 270 },
      kellerabdichtung: {
        label: "Impermeabilizzare e isolare la cantina",
        unit: "m²",
        min: 60,
        max: 75,
      },
      grundplattendaemmung: {
        label: "Isolare la platea di fondazione",
        unit: "pezzo",
        min: 2100,
        max: 4700,
      },
      haus_renovieren: {
        label: "Ristrutturare completamente una casa",
        unit: "m²",
        min: 1270,
        max: 2000,
      },
      dachbodenausbau: { label: "Ristrutturare la mansarda", unit: "m²", min: 450, max: 645 },
      betonieren: { label: "Getto di calcestruzzo", unit: "m³", min: 135, max: 250 },
      aushub: { label: "Scavo e smaltimento", unit: "m³", min: 15, max: 43 },
      erdarbeiten: { label: "Movimento terra e smaltimento", unit: "m³", min: 52, max: 63 },
      maurer: { label: "Muratura in mattoni", unit: "m²", min: 225, max: 320 },
      entruempelung: {
        label: "Sgombero durante la ristrutturazione",
        unit: "m²",
        min: 50,
        max: 65,
      },
      fenster: { label: "Finestra con montaggio", unit: "m²", min: 420, max: 680 },
      fenster_einbauen: { label: "Montare una finestra", unit: "pezzo", min: 70, max: 180 },
      fenster_reparatur: {
        label: "Ristrutturare finestra in legno",
        unit: "m²",
        min: 350,
        max: 430,
      },
      sanitaer_stunde: { label: "Lavori sanitari", unit: "h", min: 80, max: 145 },
      sanitaer_installationen: { label: "Installazioni sanitarie", unit: "m", min: 72, max: 180 },
      waermepumpe_hochtemperatur: {
        label: "Pompa di calore ad alta temperatura",
        unit: "impianto",
        min: 15000,
        max: 41000,
      },
      waermepumpe_erdwaerme: {
        label: "Pompa di calore geotermica",
        unit: "impianto",
        min: 21000,
        max: 73000,
      },
      fassadendaemmung: {
        label: "Rivestimento e isolamento facciata",
        unit: "m²",
        min: 95,
        max: 190,
      },
      fassadensanierung: { label: "Risanamento della facciata", unit: "m²", min: 120, max: 255 },
      trockenbau: { label: "Parete in cartongesso con materiale", unit: "m²", min: 48, max: 150 },
      trockenbaudecke: { label: "Controsoffitto con materiale", unit: "m²", min: 50, max: 140 },
      trockenbau_ohne_material: {
        label: "Cartongesso senza materiale",
        unit: "m²",
        min: 22,
        max: 90,
      },
      aussenputz: { label: "Intonaco esterno", unit: "m²", min: 25, max: 40 },
      innenputz: { label: "Intonaco interno", unit: "m²", min: 25, max: 40 },
      parkett_material: { label: "Parquet (materiale)", unit: "m²", min: 60, max: 100 },
      plattenleger: { label: "Lavori di piastrellista", unit: "m²", min: 36, max: 190 },
      fliesen_material: { label: "Piastrelle bagno (materiale)", unit: "m²", min: 15, max: 60 },
      laminat: { label: "Posare laminato", unit: "m²", min: 50, max: 200 },
      teppich_material: { label: "Moquette (materiale)", unit: "m²", min: 10, max: 60 },
      eichenparkett: { label: "Parquet in rovere", unit: "m²", min: 100, max: 240 },
      parkett_schleifen: {
        label: "Levigare e sigillare il parquet",
        unit: "m²",
        min: 30,
        max: 50,
      },
      schreinerei: { label: "Lavori di falegnameria", unit: "h", min: 85, max: 151 },
      kuechenrenovation: {
        label: "Ristrutturazione semplice cucina",
        unit: "forfait",
        min: 5000,
        max: 10000,
        fixed: true,
      },
      photovoltaik: {
        label: "Impianto fotovoltaico per casa",
        unit: "impianto",
        min: 16000,
        max: 28000,
        fixed: true,
      },
      batteriespeicher: { label: "Batteria con installazione", unit: "kWh", min: 600, max: 900 },
      wallbox: { label: "Collegare wallbox", unit: "pezzo", min: 770, max: 1300 },
      architektur: { label: "Architettura & progettazione", unit: "h", min: 130, max: 220 },
      bauleitung: { label: "Direzione lavori", unit: "h", min: 160, max: 230 },
      asphaltierung: { label: "Asfaltare fino a 200 m²", unit: "m²", min: 40, max: 120 },
      asphaltierung_gross: { label: "Asfaltare oltre 200 m²", unit: "m²", min: 35, max: 115 },
      strassenbau: { label: "Costruire una strada privata", unit: "ml", min: 670, max: 973 },
      rasenmaehen: { label: "Tagliare il prato", unit: "m²", min: 0.23, max: 0.55 },
      pflasterung: { label: "Pavimentare un vialetto", unit: "m²", min: 73, max: 168 },
      gartengestaltung: {
        label: "Progettazione giardino con lastre",
        unit: "m²",
        min: 120,
        max: 180,
      },
      gartengestaltung_allgemein: {
        label: "Progettazione del giardino",
        unit: "m²",
        min: 35,
        max: 110,
      },
      fassadenreinigung: {
        label: "Pulizia facciata con impregnazione",
        unit: "m²",
        min: 10,
        max: 60,
      },
      dachreinigung: {
        label: "Pulizia tetto con impregnazione",
        unit: "m²",
        min: 6,
        max: 25,
      },
      umzug_stunde: { label: "Impresa di traslochi", unit: "h", min: 78, max: 200 },
      grundreinigung: { label: "Pulizia approfondita", unit: "m²", min: 5, max: 10 },
      teppichreinigung: { label: "Pulizia della moquette", unit: "m²", min: 15, max: 30 },
      wohnungsreinigung: { label: "Pulizia dell'appartamento", unit: "h", min: 40, max: 95 },
    },
  },
} as const;

const englishPriceTerms: Record<string, string> = {
  "Malerei & Trockenbau": "Painting & drywall",
  "Innenwand streichen (Dispersion)": "Paint interior wall (emulsion)",
  "Decke streichen": "Paint ceiling",
  "Tapezieren (Raufaser)": "Wallpapering (woodchip)",
  "Fassade neu streichen": "Repaint facade",
  "Holzfenster lackieren": "Paint timber window",
  "Fassadenverkleidung und -dämmung": "Facade cladding and insulation",
  Fassadensanierung: "Facade renovation",
  "Trockenbauwand mit Material": "Drywall partition with materials",
  "Abgehängte Decke mit Material": "Suspended ceiling with materials",
  "Trockenbau ohne Material": "Drywall without materials",
  Aussenputz: "Exterior render",
  Innenputz: "Interior plaster",
  "Dach & Spengler": "Roofing & sheet metal",
  "Ziegeldach neu eindecken": "Retile pitched roof",
  "Flachdach abdichten": "Waterproof flat roof",
  "Dachfenster Velux setzen": "Install Velux roof window",
  "Dachrinne / Spenglerarbeit": "Guttering / sheet-metal work",
  "Dachisolation aufbringen": "Install roof insulation",
  "Dachboden ausbauen": "Convert attic",
  "Renovation & Umbau": "Renovation & conversion",
  "Wohnung sanieren / renovieren": "Renovate apartment",
  "Haus komplett renovieren": "Fully renovate house",
  "Entrümpelung bei Wohnungsrenovierung": "Clearance during apartment renovation",
  "Boden & Platten": "Flooring & tiling",
  "Parkett verlegen (geölt)": "Install oiled parquet",
  "Vinylboden verlegen": "Install vinyl flooring",
  "Naturstein / Plattenboden": "Natural stone / tiled floor",
  "Teppich verlegen": "Install carpet",
  "Estrich aufbringen": "Install screed",
  "Parkett (Material)": "Parquet (material)",
  Plattenlegerarbeiten: "Tiling work",
  "Badezimmerfliesen (Material)": "Bathroom tiles (material)",
  "Laminat verlegen": "Install laminate",
  "Teppichboden (Material)": "Carpet (material)",
  Eichenparkett: "Oak parquet",
  "Parkett schleifen und versiegeln": "Sand and seal parquet",
  "Fenster & Türen": "Windows & doors",
  "Holz-Alu-Fenster (Standard)": "Timber-aluminium window (standard)",
  Kunststofffenster: "uPVC window",
  "Haustür einbauen": "Install entrance door",
  "Innentür Standard": "Standard interior door",
  "Schiebetür-System": "Sliding-door system",
  "Fenster inkl. Montage": "Window including installation",
  "Fenster einbauen": "Install window",
  "Holzfenster sanieren": "Renovate timber window",
  "Sanitär & Heizung": "Plumbing & heating",
  "Bad komplett sanieren": "Complete bathroom renovation",
  "Wasserleitungen Grob- und Feininstallation": "Water pipes, first and second fix",
  "Abwasserleitung inkl. Aussenleitung": "Wastewater pipe including external section",
  "WC austauschen": "Replace toilet",
  "Sanitär-Feinmontage": "Plumbing second fix",
  "Wärmepumpe Luft/Wasser": "Air-to-water heat pump",
  "Heizkörper ersetzen": "Replace radiator",
  "Boiler ersetzen": "Replace hot-water cylinder",
  Sanitärarbeiten: "Plumbing work",
  Sanitärinstallationen: "Plumbing installations",
  "Hochtemperatur-Wärmepumpe": "High-temperature heat pump",
  "Erd- oder Grundwasserwärmepumpe": "Ground or groundwater heat pump",
  "Bau & Abbruch": "Construction & demolition",
  "Kernbohrung mit Diamantbohrer": "Diamond core drilling",
  Betonschneiden: "Concrete cutting",
  "Kernbohrung für Klimaanlage": "Core drilling for air conditioning",
  "Türöffnung in Beton schneiden": "Cut door opening in concrete",
  "Aushub / kleinere Abbrucharbeiten": "Excavation / minor demolition",
  "Einfamilienhaus abbrechen": "Demolish detached house",
  "Schotterweg anlegen": "Build gravel path",
  "Keller abdichten und isolieren": "Waterproof and insulate basement",
  "Grundplatte wasser- und wärmedämmen": "Waterproof and insulate foundation slab",
  Betonieren: "Concrete work",
  "Aushub und Abtransport": "Excavation and removal",
  "Ausgrabung und Abtransport": "Earthworks and removal",
  "Mauern mit Ziegeln": "Brickwork",
  "Elektrik & Smart Home": "Electrical & smart home",
  "Elektroinstallation im Haus": "Electrical installation in house",
  "Steckdose / Schalter montieren": "Install socket / switch",
  "Sicherung / Kleinreparatur": "Fuse / minor repair",
  "Sicherheitsnachweis SiNa": "Electrical safety certificate (SiNa)",
  "Garten & Umgebung": "Garden & outdoor areas",
  "Asphaltieren bis 200 m²": "Asphalting up to 200 m²",
  "Asphaltieren über 200 m²": "Asphalting over 200 m²",
  "Privatstrasse bauen": "Build private road",
  "Rasen mähen": "Mow lawn",
  "Einfahrt pflastern": "Pave driveway",
  "Gartengestaltung mit Steinplatten": "Garden landscaping with stone slabs",
  Gartengestaltung: "Garden landscaping",
  "Reinigung & Umzug": "Cleaning & moving",
  Reinigungskraft: "Cleaner",
  "Endreinigung 3-Zimmer-Wohnung": "Final cleaning, 3-room apartment",
  "Endreinigung Einfamilienhaus": "Final cleaning, detached house",
  "Umzug mit Zügelunternehmen": "Move with a moving company",
  Umzugshelfer: "Moving assistant",
  "Transporter mieten": "Hire van",
  Zügelunternehmen: "Moving company",
  Grundreinigung: "Deep cleaning",
  Teppichreinigung: "Carpet cleaning",
  Wohnungsreinigung: "Apartment cleaning",
  "Dachreinigung mit Imprägnierung": "Roof cleaning with protective treatment",
  "Fassadenreinigung mit Imprägnierung": "Facade cleaning with protective treatment",
  "Küche & Schreinerei": "Kitchens & carpentry",
  "Einfache Küchenrenovation": "Basic kitchen renovation",
  "Küchenrenovation inkl. Geräte": "Kitchen renovation including appliances",
  "Kompletter Küchenumbau": "Complete kitchen conversion",
  Küchenmontage: "Kitchen installation",
  Schreinerarbeiten: "Carpentry work",
  "Einbauschrank nach Mass": "Bespoke built-in wardrobe",
  "Photovoltaik & Energie": "Solar & energy",
  "Photovoltaikanlage Einfamilienhaus (ca. 8–12 kWp)":
    "Solar system for detached house (approx. 8–12 kWp)",
  "Photovoltaikanlage Einfamilienhaus": "Solar system for detached house",
  "Batteriespeicher inkl. Installation": "Battery storage including installation",
  Batteriespeicher: "Battery storage",
  "Wallbox anschliessen": "Connect EV charger",
  "Planung & Bauleitung": "Planning & site management",
  "Architektur & Projektplanung": "Architecture & project planning",
  Bauleitung: "Site management",
  Bauberatung: "Construction consulting",
  Innenmalerei: "Interior painting",
  "Fassade streichen": "Paint facade",
  "Parkett verlegen": "Install parquet",
  "Dach neu eindecken": "Retile roof",
  "Bad sanieren (pauschal)": "Bathroom renovation (flat rate)",
  Kernbohrung: "Core drilling",
  Sanitärinstallation: "Plumbing installation",
  Elektroinstallation: "Electrical installation",
};

const englishPriceUnits: Record<string, string> = {
  Stück: "item",
  Pauschal: "flat rate",
  Anlage: "system",
  Tag: "day",
  lfm: "lin. m",
};

const englishPriceCopy = {
  ...priceCopy.de,
  locale: "en-CH",
  eyebrow: "Guide prices",
  title: "How much will your construction project cost?",
  intro:
    "Transparent guide prices for construction and renovation work in Switzerland. Estimate your project costs in advance, then request specific quotes.",
  calculatorEyebrow: "Construction cost calculator",
  calculatorTitle: "A quick estimate in seconds",
  calculatorText:
    "Choose the service and scope to calculate an indicative price range based on current Swiss market data.",
  quoteEyebrow: "Need more accurate figures?",
  quoteTitle: "Request genuine quotes free of charge",
  quoteText:
    "Describe your project and several verified contractors in your region will send you binding proposals.",
  quoteButton: "Request quotes",
  tableEyebrow: "Price tables",
  tableTitle: "Guide prices by trade",
  tableText: "All prices are gross values in CHF and serve as guidance for all of Switzerland.",
  tableHeads: ["Service", "Unit", "From (CHF)", "To (CHF)"],
  calcLabels: {
    service: "Service",
    amount: "Quantity",
    estimate: "Estimated price range",
    between: "to",
    note: "Indicative estimate. Actual quotes may vary depending on scope, materials and region.",
  },
  tabs: priceCopy.de.tabs.map((tab) => ({
    ...tab,
    label: englishPriceTerms[tab.label] ?? tab.label,
    rows: tab.rows.map((row) => ({
      ...row,
      service: englishPriceTerms[row.service] ?? row.service,
      unit: englishPriceUnits[row.unit] ?? row.unit,
      ...("note" in row && row.note
        ? { note: row.note === "inkl. Gerüst" ? "incl. scaffolding" : row.note }
        : {}),
    })),
  })),
  calcOptions: Object.fromEntries(
    Object.entries(priceCopy.de.calcOptions).map(([key, option]) => [
      key,
      {
        ...option,
        label: englishPriceTerms[option.label] ?? option.label,
        unit: englishPriceUnits[option.unit] ?? option.unit,
      },
    ]),
  ),
} as unknown as typeof priceCopy.de;

const localizedPriceCopy = { ...priceCopy, en: englishPriceCopy } as const;

function PreisePage() {
  const { language } = useLanguage();
  const search = Route.useSearch();
  const copy = localizedPriceCopy[language];
  const initialTab = copy.tabs.some((tab) => tab.id === search.tab) ? search.tab : copy.tabs[0].id;

  return (
    <>
      {/* Header */}
      <section className="border-b border-border bg-surface">
        <div className="container-page py-12 md:py-16">
          <SectionHeading
            eyebrow={copy.eyebrow}
            title={copy.title}
            description={copy.intro}
            level="page"
            className="max-w-2xl"
          />
        </div>
      </section>

      {/* Calculator */}
      <section className="bg-background py-10 md:py-12">
        <div className="container-page">
          <Calculator className="hidden" />
          <div className="grid gap-7 rounded-2xl border border-border bg-card p-4 shadow-card sm:p-6 md:p-10 lg:grid-cols-[1.1fr_1fr]">
            <div>
              <div className="flex items-center gap-2 text-brand-deep">
                <Calculator className="h-5 w-5" />
                <span className="text-sm font-semibold uppercase tracking-wider">
                  {copy.calculatorEyebrow}
                </span>
              </div>
              <h2 className="mt-3 font-display text-2xl font-bold md:text-3xl">
                {copy.calculatorTitle}
              </h2>
              <p className="mt-3 text-muted-foreground">{copy.calculatorText}</p>
              <CostCalculator copy={copy} initialService={search.service} />
            </div>
            <div className="rounded-2xl bg-gradient-brand p-5 text-primary-foreground shadow-brand sm:p-8">
              <p className="text-sm font-semibold uppercase tracking-wider opacity-80">
                {copy.quoteEyebrow}
              </p>
              <h3 className="mt-2 font-display text-2xl font-bold">{copy.quoteTitle}</h3>
              <p className="mt-3 opacity-90">{copy.quoteText}</p>
              <Button
                asChild
                size="lg"
                variant="secondary"
                className="mt-6 w-full font-semibold sm:w-auto"
              >
                <Link to="/auftrag-einstellen">
                  {copy.quoteButton}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Price tables */}
      <section className="bg-surface py-12 md:py-16">
        <div className="container-page">
          <SectionHeading
            eyebrow={copy.tableEyebrow}
            title={copy.tableTitle}
            description={copy.tableText}
            align="center"
            className="mx-auto max-w-2xl"
          />

          <Tabs defaultValue={initialTab} className="mt-8 md:mt-10">
            <TabsList className="-mx-4 flex h-auto snap-x justify-start gap-2 overflow-x-auto bg-transparent px-4 pb-2 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:justify-center sm:px-0 [&::-webkit-scrollbar]:hidden">
              {copy.tabs.map((t) => {
                const Icon = priceTabIcons[t.id];
                return (
                  <TabsTrigger
                    key={t.id}
                    value={t.id}
                    className="shrink-0 snap-start rounded-full border border-border bg-card px-5 py-2 text-sm font-semibold data-[state=active]:border-brand data-[state=active]:bg-brand data-[state=active]:text-primary-foreground"
                  >
                    <Icon className="mr-2 h-4 w-4" />
                    {t.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {copy.tabs.map((t) => {
              const rows = t.rows as readonly PriceRow[];

              return (
                <TabsContent key={t.id} value={t.id} className="mt-8">
                  <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
                    <Table className="min-w-[560px]">
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead className="font-semibold text-foreground">
                            {copy.tableHeads[0]}
                          </TableHead>
                          <TableHead className="font-semibold text-foreground">
                            {copy.tableHeads[1]}
                          </TableHead>
                          <TableHead className="text-right font-semibold text-foreground">
                            {copy.tableHeads[2]}
                          </TableHead>
                          <TableHead className="text-right font-semibold text-foreground">
                            {copy.tableHeads[3]}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rows.map((r) => (
                          <TableRow key={r.service}>
                            <TableCell>
                              <p className="font-medium text-foreground">{r.service}</p>
                              {r.note && <p className="text-xs text-muted-foreground">{r.note}</p>}
                            </TableCell>
                            <TableCell className="text-muted-foreground">{r.unit}</TableCell>
                            <TableCell className="text-right font-semibold text-foreground">
                              {r.min.toLocaleString(copy.locale)}
                            </TableCell>
                            <TableCell className="text-right font-semibold text-brand-deep">
                              {r.max.toLocaleString(copy.locale)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </div>
      </section>
    </>
  );
}

type PriceCopy = (typeof localizedPriceCopy)[keyof typeof localizedPriceCopy];

function CostCalculator({ copy, initialService }: { copy: PriceCopy; initialService?: string }) {
  const initialType =
    initialService && initialService in copy.calcOptions ? initialService : "innenmalerei";
  const [type, setType] = useState(initialType);
  const [amount, setAmount] = useState("50");
  const opt = copy.calcOptions[type as keyof typeof copy.calcOptions];
  const qty = Number(amount) || 0;
  const isFixed = "fixed" in opt && opt.fixed;
  const min = isFixed ? opt.min : opt.min * qty;
  const max = isFixed ? opt.max : opt.max * qty;

  return (
    <div className="mt-6 space-y-4">
      <div>
        <Label className="text-sm font-medium">{copy.calcLabels.service}</Label>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="mt-2 h-12">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(copy.calcOptions).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {!isFixed && (
        <div>
          <Label className="text-sm font-medium">
            {copy.calcLabels.amount} ({opt.unit})
          </Label>
          <Input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="mt-2 h-12"
            min="1"
          />
        </div>
      )}
      <div className="rounded-xl border border-brand/30 bg-brand-soft p-5">
        <p className="text-sm font-semibold text-brand-deep">{copy.calcLabels.estimate}</p>
        <p className="mt-1 font-display text-3xl font-bold text-foreground">
          CHF {min.toLocaleString(copy.locale)} {copy.calcLabels.between}{" "}
          {max.toLocaleString(copy.locale)}
        </p>
        <p className="mt-2 text-xs text-muted-foreground">{copy.calcLabels.note}</p>
      </div>
    </div>
  );
}
